import { useToken } from './useToken'
import Cookies from 'js-cookie'
import { clearRedirectUrl } from '@app-common'

const ALLOWED_LANGS = ['ru', 'en'] as const
type Lang = (typeof ALLOWED_LANGS)[number]

const getValidLang = (lang?: string): Lang => {
  return ALLOWED_LANGS.includes(lang as Lang) ? (lang as Lang) : 'en'
}

/**
 * replace lang:
 * ru → с префиксом
 * en → БЕЗ префикса
 */
export const replaceLangInUrl = (
  url: string,
  preferredLanguage?: string
): string => {
  const lang = getValidLang(preferredLanguage)

  const isRootLang = /^https?:\/\/[^/]+\/(ru|en)(?=\/|$|\?|#)/

  if (isRootLang.test(url)) {
    return url
      .replace(isRootLang, match =>
        lang === 'en'
          ? match.replace(/\/(ru|en)/, '')
          : match.replace(/\/(ru|en)/, `/${lang}`)
      )
      .replace(/([^:]\/)\/+/g, '$1')
  }

  const deepLang = /\/(ru|en)(?=\/|$|\?|#)/

  if (deepLang.test(url)) {
    return url.replace(deepLang, `/${lang}`)
  }

  return lang === 'en' ? url : url.replace(/\/$/, '') + `/${lang}`
}

// Path fragment of the last step of order creation - the one exception where
// a client is sent back to where they were instead of the Workspace.
const ORDER_STEP_3_PATH = '/orders/new/step-3'

export const useRedirectAfterLogin = () => {
  const { getToken, getUserRolesFromToken } = useToken()

  const redirect = (query?: string, preferredLanguage?: string) => {
    const token = getToken()
    if (!token) return

    const roles = getUserRolesFromToken()
    const isAdmin = roles.includes('LEX_ADMIN')
    const isClient = roles.includes('LEX_CLIENT')
    const isMediator = roles.includes('LEX_MEDIATOR')

    const redirectUrl = Cookies.get('redirect_url')
    const isClientFromStep3 =
      isClient && !!redirectUrl?.includes(ORDER_STEP_3_PATH)

    const dashboardBaseUrl = (
      import.meta.env.VITE_APP_URL as string
    ).replace(/\/dashboard\/.*/, '/dashboard')

    // Every authenticated user (Client, Executor or Mediator) lands on their
    // Workspace after login, except: admins always go to the profile, and a
    // Client returning mid-way through order creation goes back to that step.
    let baseUrl: string
    if (isAdmin) {
      baseUrl = `${dashboardBaseUrl}/en/profile`
    } else if (isClientFromStep3) {
      baseUrl = redirectUrl as string
    } else if (isMediator) {
      baseUrl = `${dashboardBaseUrl}/en/disputes`
    } else {
      baseUrl = `${dashboardBaseUrl}/en/${isClient ? 'orders/client' : 'orders/lawyer'}`
    }

    // Routes inside this app (e.g. /identity/verification/...) never carry a
    // language segment, so applying replaceLangInUrl would append a bogus
    // /ru or /en and break the redirect back to them.
    const isIdentityInternalUrl = /\/identity(\/|$)/.test(baseUrl)
    const url = isIdentityInternalUrl
      ? baseUrl
      : replaceLangInUrl(baseUrl, preferredLanguage)

    const queryString = query ? `?${query}` : ''

    clearRedirectUrl()
    Cookies.remove('locale')
    Cookies.set('locale', preferredLanguage || 'en')
    window.location.href = `${url}${queryString}`
  }

  return { redirect }
}
